import {
	SIMPLE_STRUCTURE_TYPE,
	type TSimpleStructureType,
	isParametricStructure,
	isSimpleStructureType,
} from "@/models/objects/solidMesh/types";
import { ObjectType } from "@/models/objects/types";
/**
 * useSolidMeshEdit
 *
 * Shared composable for SolidMesh sub-panels (Manual, SinglePoint, DoublePoint).
 * Provides common reactive state, computed properties, and methods for editing.
 */
import { type Ref, computed, onBeforeUnmount, reactive, ref, watch } from "vue";
import type { Store } from "vuex";
import type { SolidMeshAxisUnitConfig } from "./solidMeshUnit.types";
import {
	formatDimensionValue,
	getDimensionConfig,
} from "./useSolidMeshDimensionConfig";
import { useSolidMeshUnitDisplay } from "./useSolidMeshUnitDisplay";
import type {
	SimpleStructureEditConstraintSet,
	SimpleStructureSpinnerConstraint,
} from "./simpleStructureInputConstraints.types";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
import type { PlainCartesian3 as TPoint3 } from "earthbrain-viewer";
type TColor = { red: number; green: number; blue: number };

export type TSolidMeshEditData = {
	transparency: number;
	dimensions: TPoint3;
	color: TColor;
	localAnchorPoint: TPoint3;
};

/** Number values for input fields to enable Backspace editing. */
export interface TSolidMeshCommonRawInputs {
	transparency: number | undefined;
	dimensionX: number | undefined;
	dimensionY: number | undefined;
	dimensionZ: number | undefined;
	colorR: number | undefined;
	colorG: number | undefined;
	colorB: number | undefined;
	anchorX: number | undefined;
	anchorY: number | undefined;
	anchorZ: number | undefined;
}

type TDimensionValidationRawInputs = {
	dimensionX: number | undefined;
	dimensionY: number | undefined;
	dimensionZ: number | undefined;
} & Record<string, number | undefined>;

interface IUseDimensionValidationParams {
	rawInputs: TDimensionValidationRawInputs;
	isEditing: Ref<boolean>;
	structureType: Ref<string | undefined>;
	store: Pick<Store<unknown>, "commit">;
}

export const SIMPLE_STRUCTURE_DIMENSION_MAX = 999.99;
export const SIMPLE_STRUCTURE_DIMENSION_MIN = 0.01;
export const SIMPLE_STRUCTURE_COMMON_CONSTRAINT_MAX_VALUE = 9999.99;
/** Decimal places shown for coordinate inputs, regardless of the display unit. */
const ANCHOR_POINT_DECIMALS = 3;
/** Step matches the smallest digit ANCHOR_POINT_DECIMALS displays. */
const ANCHOR_POINT_STEP = 0.001;

export const SIMPLE_STRUCTURE_EDIT_CONSTRAINTS = {
	azimuth: { min: -180, max: 180, step: 1, decimalLength: 0 },
	rotation: { min: -180, max: 180, step: 1, decimalLength: 0 },
	transparency: { min: 0, max: 100, step: 1, decimalLength: 0 },
	colorChannel: { min: 0, max: 255, step: 1, decimalLength: 0 },
	anchorPoint: {
		step: ANCHOR_POINT_STEP,
		decimalLength: ANCHOR_POINT_DECIMALS,
	},
} as const satisfies SimpleStructureEditConstraintSet;

// ---------------------------------------------------------------------------
// Composable
// ---------------------------------------------------------------------------
export function useSolidMeshEdit(
	solidMeshId: Ref<string>,
	isEditing: Ref<boolean>,
	store: Store<unknown>,
	t: (key: string) => string,
) {
	// ── Core state ──────────────────────────────────────────────────────────
	const editData = reactive<TSolidMeshEditData>({
		transparency: 0,
		dimensions: { x: 0, y: 0, z: 0 },
		color: { red: 0, green: 0, blue: 0 },
		localAnchorPoint: { x: 0, y: 0, z: 0 },
	});
	const localAnchorPoint = reactive<TPoint3>({ x: 0, y: 0, z: 0 });
	const isUpdatingPosition = ref(false);
	const anchorFetchTimeout = ref<ReturnType<typeof setTimeout> | null>(null);

	// ── Computed ─────────────────────────────────────────────────────────────
	const solidMesh = computed(
		() =>
			(
				store.state as {
					object: { objects: Array<{ type: string; id: string }> };
				}
			).object.objects.find(
				(obj) =>
					obj.type === ObjectType.SOLID_MESH && obj.id === solidMeshId.value,
			) as Record<string, unknown> | undefined,
	);

	const structureType = computed<string | undefined>(() => {
		const meta = solidMesh.value?.metaData as
			| Record<string, unknown>
			| undefined;
		const attrs = (meta?.attributes ?? solidMesh.value?.attributes) as
			| Record<string, unknown>
			| undefined;
		return attrs?.structureType as string | undefined;
	});

	const simpleStructureType = computed<TSimpleStructureType | undefined>(() => {
		const st = structureType.value;
		if (!isSimpleStructureType(st)) {
			return undefined;
		}
		return st;
	});

	const isSimpleStructure = computed(() =>
		isParametricStructure(simpleStructureType.value),
	);

	const panelTitle = computed(() => {
		if (!simpleStructureType.value) {
			return t("CUSTOM_STRUCTURE_DETAILS");
		}
		const titleMap: Record<TSimpleStructureType, string> = {
			[SIMPLE_STRUCTURE_TYPE.PIPE]: "PIPE_DETAILS",
			[SIMPLE_STRUCTURE_TYPE.BOX_CULVERT]: "BOX_CULVERT_DETAILS",
			[SIMPLE_STRUCTURE_TYPE.RETAINING_WALL]: "RETAINING_WALL_DETAILS",
			[SIMPLE_STRUCTURE_TYPE.MANHOLE]: "MANHOLE_DETAILS",
			[SIMPLE_STRUCTURE_TYPE.MANUAL]: "CUSTOM_STRUCTURE_DETAILS",
		};
		return t(titleMap[simpleStructureType.value]);
	});

	const unitTransform = useSolidMeshUnitDisplay();

	const typeConfig = computed(() =>
		getDimensionConfig(structureType.value || "", t, unitTransform),
	);

	const dimensionXLabel = computed(() => {
		const cfg = typeConfig.value;
		return isEditing.value ? `${cfg.x.label} ` : cfg.x.label;
	});
	const dimensionYLabel = computed(() => {
		const cfg = typeConfig.value;
		return isEditing.value ? `${cfg.y.label} ` : cfg.y.label;
	});
	const dimensionZLabel = computed(() => {
		const cfg = typeConfig.value;
		return isEditing.value ? `${cfg.z.label} ` : cfg.z.label;
	});
	const dimensionXStep = computed(() => SIMPLE_STRUCTURE_DIMENSION_MIN);
	const dimensionYStep = computed(() => SIMPLE_STRUCTURE_DIMENSION_MIN);
	const dimensionZStep = computed(() => SIMPLE_STRUCTURE_DIMENSION_MIN);

	const scaledDimensions = computed(() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const config = sm?.shapeConfig as
			| { dimensions: TPoint3; scale: TPoint3 }
			| undefined;
		if (!config) return { x: 0, y: 0, z: 0 };
		// Exact metres. Rounding belongs to the display boundary. Rounding here
		// reached the persisted value through the write-back, which showed up as a
		// 1 cm quantisation (30 ft came back as 29.99 ft).
		return {
			x: config.dimensions.x * config.scale.x,
			y: config.dimensions.y * config.scale.y,
			z: config.dimensions.z * config.scale.z,
		};
	});

	const formattedDimensionX = computed(() =>
		formatDimensionValue(
			Number(scaledDimensions.value.x),
			typeConfig.value.x,
			unitTransform.convertLength,
		),
	);
	const formattedDimensionY = computed(() =>
		formatDimensionValue(
			Number(scaledDimensions.value.y),
			typeConfig.value.y,
			unitTransform.convertLength,
		),
	);

	/** Formats an internal metre coordinate for read-only display in the site unit. */
	function formatCoordinate(meters: number): string {
		const displayed = unitTransform.convertLength(
			meters,
			"m",
			"site",
			ANCHOR_POINT_DECIMALS,
		);
		return `${displayed.toFixed(ANCHOR_POINT_DECIMALS)} ${unitTransform.distanceLabel.value}`;
	}

	const editColorString = computed(
		() =>
			`rgb(${editData.color.red}, ${editData.color.green}, ${editData.color.blue})`,
	);

	const pickerColor = computed({
		get: () => ({
			r: editData.color.red,
			g: editData.color.green,
			b: editData.color.blue,
		}),
		set: (_val: { r: number; g: number; b: number }) => {
			// handled by onPickerColorChange
		},
	});

	const isDraggingSolidMesh = computed(
		() =>
			(store.getters as Record<string, unknown>)[
				"solidMesh/isDraggingSolidMesh"
			] as boolean,
	);
	const preDragLocalZ = computed(
		() =>
			(store.getters as Record<string, unknown>)["solidMesh/preDragLocalZ"] as
				| number
				| undefined,
	);

	const anchorType = computed(() => {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		const attrs = sm?.attributes as Record<string, unknown> | undefined;
		return (attrs?.anchorType as string) ?? "bottom-nw";
	});

	const displayLocalZ = computed(() => {
		if (isDraggingSolidMesh.value && preDragLocalZ.value !== undefined) {
			return preDragLocalZ.value;
		}
		return editData.localAnchorPoint.z;
	});

	// ── Helper: get rotation degrees from viewer ────────────────────────────
	async function getRotationDegrees(meshId: string): Promise<TPoint3> {
		try {
			return (
				((await store.dispatch("solidMesh/getSolidMeshRotationDegrees", {
					meshId,
				})) as TPoint3) || { x: 0, y: 0, z: 0 }
			);
		} catch {
			return { x: 0, y: 0, z: 0 };
		}
	}

	// ── Methods ──────────────────────────────────────────────────────────────
	async function fetchLocalAnchorPoint() {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm || !(sm.isLoaded as boolean)) {
			Object.assign(localAnchorPoint, { x: 0, y: 0, z: 0 });
			return;
		}
		try {
			const anchor = await (sm.getLocalAnchorPoint as () => Promise<TPoint3>)();
			Object.assign(localAnchorPoint, anchor);
		} catch (error: unknown) {
			console.error("Failed to fetch local anchor point:", error);
			Object.assign(localAnchorPoint, { x: 0, y: 0, z: 0 });
		}
	}

	function getColorString(color: { red: number; green: number; blue: number }) {
		const r = Math.round(color.red * 255);
		const g = Math.round(color.green * 255);
		const b = Math.round(color.blue * 255);
		return `rgb(${r}, ${g}, ${b})`;
	}

	/**
	 * Converts scaled metre dimensions into each axis base unit (m or mm).
	 * editData stays in internal units because derived calculations read it;
	 * the site unit is applied only when filling rawInputs.
	 */
	function buildEditDimensions(
		scaledX: number,
		scaledY: number,
		scaledZ: number,
	) {
		const cfg = typeConfig.value;
		// No rounding here: editData feeds the viewer write-back, so a rounded value
		// would be persisted. Rounding belongs to the rawInputs boundary only.
		const toAxis = (meters: number, axis: SolidMeshAxisUnitConfig) =>
			unitTransform.convertLength(meters, "m", axis.baseUnit);
		return {
			x: toAxis(scaledX, cfg.x),
			y: toAxis(scaledY, cfg.y),
			z: toAxis(scaledZ, cfg.z),
		};
	}

	function syncDimensionsFromViewer(gradientPct?: number) {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm) return;
		const config = sm.shapeConfig as { dimensions: TPoint3; scale: TPoint3 };
		const dims = buildEditDimensions(
			config.dimensions.x * config.scale.x,
			config.dimensions.y * config.scale.y,
			config.dimensions.z * config.scale.z,
		);
		Object.assign(editData.dimensions, dims);
		// Retaining-wall: editData.dimensions.z shows vertical height (not slope height).
		if (structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL) {
			const gPct = gradientPct ?? 0;
			const roll = Math.atan(gPct / 100);
			const slopeH = config.dimensions.z * config.scale.z;
			editData.dimensions.z = slopeH * Math.cos(roll);
		}
	}

	async function loadCommonEditData() {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm || !(sm.isLoaded as boolean)) return;
		const config = sm.shapeConfig as {
			color: { red: number; green: number; blue: number; alpha: number };
		};
		editData.transparency = Math.round(config.color.alpha * 100);
		Object.assign(
			editData.dimensions,
			buildEditDimensions(
				Number(scaledDimensions.value.x),
				Number(scaledDimensions.value.y),
				Number(scaledDimensions.value.z),
			),
		);
		editData.color = {
			red: Math.round(config.color.red * 255),
			green: Math.round(config.color.green * 255),
			blue: Math.round(config.color.blue * 255),
		};
		await fetchLocalAnchorPoint();
		// Kept as exact metres. Rounding happens only when filling rawInputs, so an
		// edit to one axis never writes a rounded value for the other two.
		editData.localAnchorPoint = {
			x: localAnchorPoint.x,
			y: localAnchorPoint.y,
			z: localAnchorPoint.z,
		};
	}

	/** Populate rawInputs with current editData values. Call after loadCommonEditData. */
	function initCommonRawInputs(rawInputs: TSolidMeshCommonRawInputs) {
		rawInputs.transparency = editData.transparency;
		// Internal units throughout: DistanceSettingInput owns the conversion.
		rawInputs.dimensionX = editData.dimensions.x;
		rawInputs.dimensionY = editData.dimensions.y;
		rawInputs.dimensionZ = editData.dimensions.z;
		rawInputs.colorR = editData.color.red;
		rawInputs.colorG = editData.color.green;
		rawInputs.colorB = editData.color.blue;
		rawInputs.anchorX = editData.localAnchorPoint.x;
		rawInputs.anchorY = editData.localAnchorPoint.y;
		rawInputs.anchorZ = editData.localAnchorPoint.z;
	}

	async function onTransparencyChange() {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm) return;
		const transparency =
			Math.max(0, Math.min(100, editData.transparency)) / 100;
		// Suppress wgs84Center watcher — appearance changes don't move the mesh,
		// but updateFromViewer() re-serializes wgs84Center causing Z drift.
		isUpdatingPosition.value = true;
		try {
			await (sm.setTransparency as (v: number) => Promise<void>)(transparency);
		} finally {
			setTimeout(() => {
				isUpdatingPosition.value = false;
			}, 0);
		}
	}

	function onPickerColorChange(val: unknown) {
		if (typeof val === "object" && val !== null) {
			const v = val as { r: number; g: number; b: number };
			editData.color.red = v.r;
			editData.color.green = v.g;
			editData.color.blue = v.b;
		}
		onColorChange();
	}

	async function onColorChange() {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm) return;
		const color = {
			red: Math.max(0, Math.min(255, editData.color.red)) / 255,
			green: Math.max(0, Math.min(255, editData.color.green)) / 255,
			blue: Math.max(0, Math.min(255, editData.color.blue)) / 255,
		};
		isUpdatingPosition.value = true;
		try {
			await (
				sm.setColor as (c: {
					red: number;
					green: number;
					blue: number;
				}) => Promise<void>
			)(color);
		} finally {
			setTimeout(() => {
				isUpdatingPosition.value = false;
			}, 0);
		}
	}

	// ── Anchor editing (for SinglePoint + Manual) ────────────────────────────
	function updateLocalAnchorPointX(val: number | string) {
		if (val === "") return;
		const num = Number(val);
		if (!Number.isFinite(num)) return;
		editData.localAnchorPoint.x = num;
		onLocalAnchorPointChange();
	}
	function updateLocalAnchorPointY(val: number | string) {
		if (val === "") return;
		const num = Number(val);
		if (!Number.isFinite(num)) return;
		editData.localAnchorPoint.y = num;
		onLocalAnchorPointChange();
	}
	function updateLocalAnchorPointZ(val: number | string) {
		if (val === "") return;
		const num = Number(val);
		if (!Number.isFinite(num)) return;
		editData.localAnchorPoint.z = num;
		onLocalAnchorPointChange();
	}
	async function onLocalAnchorPointChange() {
		const sm = solidMesh.value as Record<string, unknown> | undefined;
		if (!sm || isUpdatingPosition.value) return;
		isUpdatingPosition.value = true;
		try {
			await (sm.setLocalPosition as (p: TPoint3) => Promise<void>)({
				...editData.localAnchorPoint,
			});
			await fetchLocalAnchorPoint();
		} finally {
			// Release lock on next tick so the watcher doesn't re-enter
			setTimeout(() => {
				isUpdatingPosition.value = false;
			}, 0);
		}
	}

	// ── Common watchers ─────────────────────────────────────────────────────
	watch(
		solidMesh,
		async (newVal) => {
			const sm = newVal as Record<string, unknown> | undefined;
			if (sm && !(sm.isLoaded as boolean)) {
				try {
					await (sm.loadInViewer as () => Promise<void>)();
					await fetchLocalAnchorPoint();
				} catch (error: unknown) {
					console.error("Failed to load SolidMesh in viewer:", error);
				}
			} else if (sm?.isLoaded) {
				await fetchLocalAnchorPoint();
			}
		},
		{ immediate: true },
	);

	// ── Cleanup ─────────────────────────────────────────────────────────────
	onBeforeUnmount(() => {
		if (anchorFetchTimeout.value) clearTimeout(anchorFetchTimeout.value);
	});

	return {
		// State
		editData,
		localAnchorPoint,
		isUpdatingPosition,
		anchorFetchTimeout,
		// Computed
		solidMesh,
		structureType,
		isSimpleStructure,
		panelTitle,
		typeConfig,
		// Exposed so sub-panels format their own axes through the same boundary.
		convertLength: unitTransform.convertLength,
		formatCoordinate,
		dimensionXLabel,
		dimensionYLabel,
		dimensionZLabel,
		dimensionXStep,
		dimensionYStep,
		dimensionZStep,
		scaledDimensions,
		formattedDimensionX,
		formattedDimensionY,
		editColorString,
		pickerColor,
		isDraggingSolidMesh,
		preDragLocalZ,
		anchorType,
		displayLocalZ,
		// Methods
		fetchLocalAnchorPoint,
		getColorString,
		buildEditDimensions,
		syncDimensionsFromViewer,
		loadCommonEditData,
		initCommonRawInputs,
		onTransparencyChange,
		onPickerColorChange,
		onColorChange,
		updateLocalAnchorPointX,
		updateLocalAnchorPointY,
		updateLocalAnchorPointZ,
		onLocalAnchorPointChange,
		getRotationDegrees,
	};
}

export function useInputActivity(timeoutMs = 500) {
	const isDimensionInputting = ref(false);
	const isDimensionFieldFocused = ref(false);
	const dimensionInputTypingTimeout = ref(null);

	function markDimensionInputting() {
		isDimensionInputting.value = true;
		if (dimensionInputTypingTimeout.value) {
			clearTimeout(dimensionInputTypingTimeout.value);
		}
		dimensionInputTypingTimeout.value = setTimeout(() => {
			if (!isDimensionFieldFocused.value) {
				isDimensionInputting.value = false;
			}
			dimensionInputTypingTimeout.value = null;
		}, timeoutMs);
	}

	function onDimensionInputFocus() {
		isDimensionFieldFocused.value = true;
		isDimensionInputting.value = true;
	}

	function onDimensionInputBlur() {
		isDimensionFieldFocused.value = false;
		if (dimensionInputTypingTimeout.value) {
			clearTimeout(dimensionInputTypingTimeout.value);
			dimensionInputTypingTimeout.value = null;
		}
		setTimeout(() => {
			if (!isDimensionFieldFocused.value) {
				isDimensionInputting.value = false;
			}
		}, 0);
	}
	return {
		isDimensionInputting,
		isDimensionFieldFocused,
		dimensionInputTypingTimeout,
		markDimensionInputting,
		onDimensionInputFocus,
		onDimensionInputBlur,
	};
}

export function getMaxDecimals(maxValue: number): number {
	return String(maxValue).split(".")[1]?.length ?? 0;
}

type TOptionalNumberOptions = {
	emptyValue?: number;
};

export function toOptionalNumber(
	val: unknown,
	options?: Readonly<TOptionalNumberOptions>,
): number | undefined {
	if (val === null || val === undefined) return options?.emptyValue;
	if (typeof val === "string" && val.trim() === "") return options?.emptyValue;
	const parsed = Number(val);
	return Number.isFinite(parsed) ? parsed : undefined;
}

/** 指定した入力制約で数値を丸め・境界化して返す。 */
export function normalizeSpinnerInput(
	value: number,
	constraint: Readonly<SimpleStructureSpinnerConstraint>,
): number {
	let normalized = value;
	if (constraint && constraint.min !== undefined) {
		normalized = Math.max(constraint.min, normalized);
	}
	if (constraint && constraint.max !== undefined) {
		normalized = Math.min(constraint.max, normalized);
	}
	if (constraint && constraint.decimalLength !== undefined) {
		const factor = 10 ** constraint.decimalLength;
		normalized = Math.round(normalized * factor) / factor;
	}
	return normalized === 0 ? 0 : normalized;
}

export function useDimensionValidation({
	rawInputs,
	isEditing,
	structureType,
	store,
}: IUseDimensionValidationParams) {
	// Validate: disable save button when any field is empty.
	// Range and decimal limits are owned by the input components, which clamp and
	// truncate before emitting, so only a finite number is required here.
	watch(
		rawInputs,
		() => {
			if (!isEditing.value) return;
			const hasInvalid = Object.values(rawInputs).some((v) => {
				if (v == null) return true;
				if (typeof v !== "number") return true;
				return !Number.isFinite(v);
			});
			store.commit("object/setEditFormValidBySource", {
				source:
					structureType.value === SIMPLE_STRUCTURE_TYPE.MANUAL
						? "manual-detail"
						: structureType.value === SIMPLE_STRUCTURE_TYPE.PIPE ||
								structureType.value === SIMPLE_STRUCTURE_TYPE.RETAINING_WALL
							? "double-point-detail"
							: "single-point-detail",
				isValid: !hasInvalid,
			});
		},
		{ deep: true },
	);
}
